import {
  degToRad,
  distancePointToSegment,
  localToWorld,
  type Obb2,
  type Vec2,
  type Vec3,
} from '@indusketch/geometry';
import {
  sectionOf,
  STEEL_PROFILES,
  type SectionId,
  type SteelProfileId,
} from '../steelProfiles.js';
import {
  DEFAULT_HALL_LOADS,
  DEFAULT_ROOF_LOAD,
  DEFAULT_WIND_PRESSURE,
  hallLoads,
  type HallLoads,
} from '../loads.js';
import { ridgeHeight } from './roof.js';
import { fillText, modelTexts } from '../texts.js';
import type {
  Building,
  HallStructure,
  Opening,
  ProjectDocument,
  ProjectSettings,
  Wall,
} from '../types.js';
import { formatNumber } from '../units.js';

/** Usual distance between the frames of a sandwich-panel hall. */
export const DEFAULT_BAY = 5;
/** Hall width beyond which a row of interior columns halves the rafter span (two beyond 60 m). */
export const INTERIOR_ROW_SPAN = 30;
/** Rafters and purlins stay this far under the roof face so they never show through it. */
const ROOF_GAP = 0.02;
/** Largest purlin distance along the slope (usual 1.5–2.5 m for sandwich roofs). */
const PURLIN_SPACING = 2;
/** First wall girt above the floor, then at most this far apart up to the eaves. */
const FIRST_GIRT = 2.3;
const GIRT_SPACING = 2.5;

/**
 * Pre-sizing of a haunched two-hinged frame (column, rafter) for a rafter span up to the given
 * length: about 5 m frame distance and 0.75–1.2 kN/m² snow, after the ArcelorMittal tables.
 */
const FRAME_SIZES: readonly (readonly [number, SteelProfileId, SteelProfileId])[] = [
  [8, 'IPE 240', 'IPE 240'],
  [10, 'IPE 270', 'IPE 270'],
  [12.5, 'IPE 330', 'IPE 330'],
  [15, 'IPE 400', 'IPE 400'],
  [17.5, 'IPE 450', 'IPE 400'],
  [20, 'IPE 450', 'IPE 450'],
  [22.5, 'IPE 500', 'IPE 500'],
  [25, 'IPE 550', 'IPE 500'],
  [30, 'IPE 600', 'IPE 550'],
  [Infinity, 'HEA 500', 'HEA 500'],
];

export type SteelMemberKind =
  | 'column'
  | 'rafter'
  | 'interiorColumn'
  | 'gableColumn'
  | 'beam'
  | 'purlin'
  | 'girt'
  | 'strut'
  | 'brace';

const VERTICAL: readonly SteelMemberKind[] = ['column', 'interiorColumn', 'gableColumn'];

/** One straight steel member: its axis from `a` to `b` and the direction its web points. */
export interface SteelMember {
  readonly kind: SteelMemberKind;
  readonly profile: SectionId;
  readonly a: Vec3;
  readonly b: Vec3;
  /** Unit vector across the member along the profile height. */
  readonly web: Vec3;
  /** Name of the frame axis a column stands on. */
  readonly axis?: string;
  /** Part of the roof structure (rafters, purlins, roof bracing, ridge strut), hidden on its own. */
  readonly roof?: true;
}

/** Haunch at a frame corner: a triangle in the frame plane, `thickness` wide across it. */
export interface SteelPlate {
  readonly corners: readonly [Vec3, Vec3, Vec3];
  readonly normal: Vec3;
  readonly thickness: number;
}

/** How a hall is built: steel frames, concrete columns with steel binders, or all precast. */
export type HallSystem = NonNullable<HallStructure['system']>;

/** Concrete member: a prism along a→b, `depth` along `web` and `width` across both. */
export interface ConcreteBar {
  readonly kind: SteelMemberKind;
  readonly a: Vec3;
  readonly b: Vec3;
  readonly width: number;
  readonly depth: number;
  readonly web: Vec3;
  readonly axis?: string;
  readonly roof?: true;
}

/**
 * Precast binder in the frame plane: its top along the roof and its underside, level or rising
 * towards the ridge, as points above each other from one support to the other.
 */
export interface ConcreteGirder {
  readonly top: readonly Vec3[];
  readonly bottom: readonly Vec3[];
  readonly normal: Vec3;
  /** Flange width shown; the I section has about `GIRDER_SOLIDITY` of the full outline. */
  readonly thickness: number;
}

/** Share of the full side outline × flange width a precast I binder is made of. */
export const GIRDER_SOLIDITY = 0.5;

/** Cross section of a column in plan: its outline (I or rectangle) and the box around it. */
export interface SteelColumn {
  readonly kind: SteelMemberKind;
  /** Rolled profile; null for a concrete column. */
  readonly profile: SectionId | null;
  readonly center: Vec2;
  readonly outline: readonly Vec2[];
  readonly obb: Obb2;
  readonly top: number;
  readonly axis?: string;
}

/** Grid axis through the frames (1, 2, …) or the column rows (A, B, …); `b` is the named end. */
export interface GridAxis {
  readonly label: string;
  readonly a: Vec2;
  readonly b: Vec2;
}

export interface HallFrame {
  readonly system: HallSystem;
  /** Steel members only; concrete parts are in `bars` and `girders`. */
  readonly members: readonly SteelMember[];
  readonly plates: readonly SteelPlate[];
  readonly bars: readonly ConcreteBar[];
  readonly girders: readonly ConcreteGirder[];
  readonly columns: readonly SteelColumn[];
  /** Square cross section of the concrete columns along the facade; null for steel. */
  readonly columnSize: number | null;
  /** Depth of the precast binders at the supports and at their deepest point. */
  readonly girder: { readonly support: number; readonly deepest: number } | null;
  /** Planning hints on the chosen construction (spans beyond the usual). */
  readonly hints: readonly string[];
  /** Frame axes first, then the column rows. */
  readonly axes: readonly GridAxis[];
  readonly bays: number;
  /** Distance between two frame axes. */
  readonly bayLength: number;
  readonly interiorRows: number;
  /** Interior columns stand under every n-th frame. */
  readonly interiorSpacing: number;
  /** Beam along the interior rows carrying the frames between their columns. */
  readonly beamProfile: SteelProfileId | null;
  readonly columnProfile: SteelProfileId;
  readonly rafterProfile: SteelProfileId;
  /** Whether the hall has wall girts (they push the columns inwards by their depth). */
  readonly girts: boolean;
  /** What the automatic choice would be (shown next to a value set by hand). */
  readonly auto: {
    readonly interiorRows: number;
    readonly columnProfile: SteelProfileId;
    readonly rafterProfile: SteelProfileId;
    readonly beamProfile: SteelProfileId | null;
    readonly columnSize: number | null;
    readonly girts: boolean;
  };
  /** Free span of a rafter between two column axes. */
  readonly rafterSpan: number;
  /** Snow, own weight and photovoltaics on the roof the members are sized for (kN/m²). */
  readonly roofLoad: number;
  /** Peak velocity pressure of the wind the walls' members are sized for (kN/m²). */
  readonly windLoad: number;
  /** Clear width between the frame columns and clear length between the gable steel. */
  readonly clearWidth: number;
  readonly clearLength: number;
}

/** Usual binder distance of halls on concrete columns (agricultural and precast: 6–7.5 m). */
export const CONCRETE_BAY = 7.5;
export const hallDefaultBay = (system: HallSystem): number =>
  system === 'steel' ? DEFAULT_BAY : CONCRETE_BAY;
/** Span up to which binders go without interior columns: frames, steel binders, precast. */
const ROW_SPAN: Readonly<Record<HallSystem, number>> = {
  steel: INTERIOR_ROW_SPAN,
  hybrid: INTERIOR_ROW_SPAN,
  precast: 40,
};
const autoRows = (width: number, system: HallSystem): number => {
  const span = ROW_SPAN[system];
  return width > 2 * span ? 2 : width > span ? 1 : 0;
};
/** Roof slope above which precast pitched binders are no longer the usual shape (10 %). */
const PRECAST_SLOPE = 0.1;

function frameSizes(span: number): readonly [SteelProfileId, SteelProfileId] {
  const row = FRAME_SIZES.find(([max]) => span <= max + 1e-6);
  return row ? [row[1], row[2]] : ['HEA 650', 'HEA 650'];
}

/**
 * Span the frame table is read at: the frame moment grows with its share of the roof
 * (frame distance), the roof load and the square of the span, so frames further apart than
 * the table's 5 m or under more snow count as a longer span. Lighter cases keep the table size.
 */
export const frameTableSpan = (
  span: number,
  bayLength: number,
  roofLoad: number = DEFAULT_ROOF_LOAD,
): number =>
  span * Math.sqrt(Math.max(1, (bayLength / DEFAULT_BAY) * (roofLoad / DEFAULT_ROOF_LOAD)));

/** Interior columns carry more under every second or third frame only, or under heavy snow. */
const interiorProfile = (height: number, spacing: number, roofLoad: number): SteelProfileId => {
  const sizes: readonly SteelProfileId[] = ['HEB 200', 'HEB 240', 'HEB 260', 'HEB 280', 'HEB 300'];
  const step = (height <= 8 ? 0 : 1) + (spacing - 1) + (roofLoad > 1.4 * DEFAULT_ROOF_LOAD ? 1 : 0);
  return sizes[Math.min(sizes.length - 1, step)] ?? 'HEB 300';
};

const LOAD_FACTOR = 1.4;
const STEEL_YIELD = 235e3;
const STEEL_E = 210e6;
const BEAM_CANDIDATES = (Object.keys(STEEL_PROFILES) as SteelProfileId[]).filter(
  (id) => !id.startsWith('HEB'),
);

const sectionModulus = ({ h, b, tf, tw }: { h: number; b: number; tf: number; tw: number }) => {
  const inertia = (b * h ** 3 - (b - tw) * (h - 2 * tf) ** 3) / 12;
  return { inertia, modulus: (2 * inertia) / h };
};
const sectionArea = ({ h, b, tf, tw }: { h: number; b: number; tf: number; tw: number }) =>
  2 * b * tf + (h - 2 * tf) * tw;

/**
 * Lightest IPE/HEA beam for a span `length` carrying `loads - 1` frames at equal distances
 * (each `load` kN): bending with the load factor, deflection up to L/300 without it.
 */
export function beamProfileFor(length: number, loads: number, load: number): SteelProfileId {
  const at = Array.from({ length: loads - 1 }, (_, i) => ((i + 1) * length) / loads);
  const moment = Math.max(
    0,
    ...at.map((x) =>
      at.reduce(
        (m, a) => m + load * (a <= x ? (a * (length - x)) / length : (x * (length - a)) / length),
        0,
      ),
    ),
  );
  // Deflection at midspan from each point load (by symmetry the larger half is used).
  const deflection = (inertia: number) =>
    at.reduce((d, a0) => {
      const a = Math.min(a0, length - a0);
      return d + (load * a * (3 * length ** 2 - 4 * a ** 2)) / (48 * STEEL_E * inertia);
    }, 0);
  const fits = BEAM_CANDIDATES.filter((id) => {
    const { inertia, modulus } = sectionModulus(STEEL_PROFILES[id]);
    return moment * LOAD_FACTOR <= modulus * STEEL_YIELD && deflection(inertia) <= length / 300;
  }).sort((x, y) => sectionArea(STEEL_PROFILES[x]) - sectionArea(STEEL_PROFILES[y]));
  return fits[0] ?? 'HEA 650';
}
/**
 * Lightest IPE/HEA binder spanning `span` simply supported (on concrete columns) under the
 * line load `lineLoad` (kN/m): bending with the load factor, deflection up to L/250.
 */
export function girderProfileFor(span: number, lineLoad: number): SteelProfileId {
  const moment = (lineLoad * span ** 2) / 8;
  const fits = BEAM_CANDIDATES.filter((id) => {
    const { inertia, modulus } = sectionModulus(STEEL_PROFILES[id]);
    const deflection = (5 * lineLoad * span ** 4) / (384 * STEEL_E * inertia);
    return moment * LOAD_FACTOR <= modulus * STEEL_YIELD && deflection <= span / 250;
  }).sort((x, y) => sectionArea(STEEL_PROFILES[x]) - sectionArea(STEEL_PROFILES[y]));
  return fits[0] ?? 'HEA 650';
}

const roundUp = (v: number, step: number) => Math.ceil(v / step - 1e-9) * step;
/** Load factor of the wind; share of its peak pressure on a long wall's columns, (0.8 + 0.5) / 2. */
const WIND_GAMMA = 1.5;
const WIND_SHARE = 0.65;
/** Bending a square concrete column takes: about 0.12 · b³ · fcd for C30/37 (kN/m²). */
const COLUMN_BENDING = 0.12 * 17_000;
/** Spans from which a rolled steel binder or a precast binder is no longer the usual choice. */
const TRUSS_SPAN = 30;
const PRECAST_SPAN = 40;

/** Rectangle of a concrete column in plan, its depth along the web. */
function concreteFootprint(m: ConcreteBar): SteelColumn {
  const rotation = Math.atan2(m.web.y, m.web.x);
  const center = { x: m.a.x, y: m.a.y };
  const [x, y] = [m.depth / 2, m.width / 2];
  const placement = { position: center, rotation };
  return {
    kind: m.kind,
    profile: null,
    center,
    outline: [
      { x: -x, y: -y },
      { x, y: -y },
      { x, y },
      { x: -x, y },
    ].map((q) => localToWorld(q, placement)),
    obb: { center, width: m.depth, depth: m.width, rotation },
    top: Math.max(m.a.z, m.b.z),
    ...(m.axis !== undefined ? { axis: m.axis } : {}),
  };
}

const gableProfile = (height: number): SteelProfileId =>
  height <= 6
    ? 'HEA 200'
    : height <= 8
      ? 'HEA 240'
      : height <= 10
        ? 'HEA 300'
        : height <= 12
          ? 'HEA 360'
          : 'HEA 400';
/** Cold-formed C purlins and girts span up to about 7.5 m; further apart rolled IPE take over. */
const purlinProfile = (bay: number): SectionId =>
  bay <= 5.01
    ? 'C 200'
    : bay <= 6.51
      ? 'C 250'
      : bay <= 7.51
        ? 'C 300'
        : bay <= 9.01
          ? 'IPE 220'
          : bay <= 10.51
            ? 'IPE 240'
            : 'IPE 270';
const girtProfile = (bay: number): SectionId =>
  bay <= 5.01
    ? 'C 160'
    : bay <= 6.51
      ? 'C 200'
      : bay <= 7.51
        ? 'C 250'
        : bay <= 9.01
          ? 'IPE 200'
          : bay <= 10.51
            ? 'IPE 220'
            : 'IPE 240';

/** Vertical sandwich panels and trapezoidal sheets span between girts; others need none. */
const facadeNeedsGirts = (b: Building): boolean =>
  b.facade === 'iso-vertical' || b.facade === 'trapezoid';

/** Plan cross section of a vertical member: an I with the web along `web`. */
function columnFootprint(m: SteelMember): SteelColumn {
  const { h, b, tf, tw } = sectionOf(m.profile);
  const rotation = Math.atan2(m.web.y, m.web.x);
  const center = { x: m.a.x, y: m.a.y };
  const [x, y, w, f] = [h / 2, b / 2, tw / 2, h / 2 - tf];
  const local: Vec2[] = [
    { x: -x, y: -y },
    { x: -f, y: -y },
    { x: -f, y: -w },
    { x: f, y: -w },
    { x: f, y: -y },
    { x, y: -y },
    { x, y },
    { x: f, y },
    { x: f, y: w },
    { x: -f, y: w },
    { x: -f, y },
    { x: -x, y },
  ];
  const placement = { position: center, rotation };
  return {
    kind: m.kind,
    profile: m.profile,
    center,
    outline: local.map((q) => localToWorld(q, placement)),
    obb: { center, width: h, depth: b, rotation },
    top: Math.max(m.a.z, m.b.z),
    ...(m.axis !== undefined ? { axis: m.axis } : {}),
  };
}

/** Heights of the wall girts: the first at 2.3 m, the others evenly up to below the eaves. */
function girtLevels(eave: number): number[] {
  const top = eave - 0.3;
  if (top < FIRST_GIRT + 0.3) return [Math.max(0.5, top / 2)];
  const n = Math.ceil((top - FIRST_GIRT) / GIRT_SPACING - 1e-6);
  return Array.from({ length: n + 1 }, (_, i) => FIRST_GIRT + (i * (top - FIRST_GIRT)) / n);
}

/**
 * Steel frame of a rectangular hall: frames of two columns and a rafter (two to the ridge)
 * evenly spread at most `bay` apart, interior columns under the rafters of wide halls,
 * columns in the gable walls, purlins, wall girts, bracing with eave struts in the end bays
 * and haunches at the frame corners. The columns stand inside, behind the girts (or the
 * wall). `loads` (roof load and wind pressure, kN/m², see `hallLoads`) size the members.
 * Null for offices, drawn outlines and halls without a steel frame.
 */
export function hallFrame(b: Building, loads: HallLoads = DEFAULT_HALL_LOADS): HallFrame | null {
  let byLoad = frameCache.get(b);
  if (!byLoad) frameCache.set(b, (byLoad = new Map<string, HallFrame | null>()));
  const key = `${String(loads.roof)}|${String(loads.wind)}`;
  const cached = byLoad.get(key);
  if (cached !== undefined) return cached;
  const frame = computeHallFrame(b, loads);
  byLoad.set(key, frame);
  return frame;
}

/** Buildings are immutable document values, so their frame is worked out once per load. */
const frameCache = new WeakMap<Building, Map<string, HallFrame | null>>();

/** Roof and wind loads on a hall under the project's settings, with its photovoltaics. */
export const projectHallLoads = (
  doc: { readonly settings?: Pick<ProjectSettings, 'loads'> },
  b: Building,
): HallLoads =>
  hallLoads(doc.settings, { eaveHeight: b.eaveHeight, pv: b.pv, ridge: ridgeHeight(b) });

/** The hall's frame under the loads of the project's settings. */
export const projectHallFrame = (
  doc: { readonly settings?: Pick<ProjectSettings, 'loads'> },
  b: Building,
): HallFrame | null => hallFrame(b, projectHallLoads(doc, b));

function computeHallFrame(b: Building, loads: HallLoads): HallFrame | null {
  const roofLoad = loads.roof;
  // Members facing the wind grow with its pressure over the 0.65 kN/m² of the tables.
  const windFactor = Math.max(1, loads.wind / DEFAULT_WIND_PRESSURE);
  const cfg = b.structure ?? {};
  if (b.kind !== 'hall' || b.office || b.footprint || cfg.enabled === false) return null;
  const alongX = b.width >= b.depth;
  const a = (alongX ? b.width : b.depth) / 2;
  const s = (alongX ? b.depth : b.width) / 2;
  const t = b.wallThickness;
  const e = b.eaveHeight;
  const flat = b.roofType === 'flat' || b.roofPitchDeg === 0;
  const pitch = flat ? 0 : degToRad(b.roofPitchDeg);
  const tan = Math.tan(pitch);
  const cos = Math.cos(pitch);
  const sin = Math.sin(pitch);
  const placement = { position: b.center, rotation: b.rotation };
  const origin = localToWorld({ x: 0, y: 0 }, placement);
  const p = (u: number, v: number, z: number): Vec3 => {
    const w = localToWorld(alongX ? { x: u, y: v } : { x: v, y: u }, placement);
    return { x: w.x, y: w.y, z };
  };
  const dir = (du: number, dv: number, dz = 0): Vec3 => {
    const w: Vec2 = localToWorld(alongX ? { x: du, y: dv } : { x: dv, y: du }, placement);
    return { x: w.x - origin.x, y: w.y - origin.y, z: dz };
  };

  const system: HallSystem = cfg.system ?? 'steel';
  const concrete = system !== 'steel';
  const bay = cfg.bay ?? hallDefaultBay(system);
  const purlins = cfg.purlins !== false;
  const girts = cfg.girts ?? facadeNeedsGirts(b);
  // Columns fixed in their sockets brace the walls; only steel binders need a roof bracing.
  const bracing = system !== 'precast' && cfg.bracing !== false;
  const wallBracing = system === 'steel' && bracing;
  const haunches = system === 'steel' && cfg.haunches !== false;
  // A purlin's moment grows with the load and the square of its span (the frame distance).
  const purlinId = purlinProfile(bay * Math.sqrt(Math.max(1, roofLoad / DEFAULT_ROOF_LOAD)));
  const girtId = girtProfile(bay * Math.sqrt(windFactor));
  const pd = purlins ? sectionOf(purlinId).h : 0;
  const gd = girts ? sectionOf(girtId).h : 0;

  const rows = cfg.interiorRows ?? autoRows(2 * s, system);
  // Frame distance as the hall length divides (the columns' own width left aside).
  const lengthInside = 2 * (a - t - gd);
  const bayEstimate = lengthInside / Math.max(1, Math.ceil(lengthInside / bay - 1e-6));
  // The automatic sizes follow the span the rafters really have between their supports.
  const tableSpan = frameTableSpan((2 * (s - t - gd)) / (rows + 1), bayEstimate, roofLoad);
  const [, autoRafter] = frameSizes(tableSpan);
  // Wind bends the frame columns: half its excess over the tables counts like more span.
  const [autoColumn] = frameSizes(tableSpan * Math.sqrt(1 + (windFactor - 1) / 2));
  const columnProfile = cfg.columnProfile ?? autoColumn;
  const col = STEEL_PROFILES[columnProfile];
  /** Underside of the roof covering across the span. */
  const roofAt = (v: number): number =>
    (flat ? e : b.roofType === 'mono' ? e + (v + s) * tan : e + (s - Math.abs(v)) * tan) - ROOF_GAP;
  // A concrete column fixed in its socket: about 1/18 of its height, at least 40 cm, and
  // enough for the wind on the wall `width` it carries bending it at its foot.
  const heavy = roofLoad > 1.4 * DEFAULT_ROOF_LOAD ? 0.05 : 0;
  const windSize = (height: number, width: number) => {
    const moment = (WIND_GAMMA * WIND_SHARE * loads.wind * width * height ** 2) / 2;
    return roundUp(Math.cbrt(moment / COLUMN_BENDING), 0.05);
  };
  const concreteSize = (height: number, min: number, width = 0) =>
    Math.max(min, roundUp(height / 18, 0.05), windSize(height, width)) + heavy;
  const autoColumnSize = concrete ? concreteSize(e, 0.4, bayEstimate) : null;
  const columnSize = concrete ? (cfg.concreteColumn ?? autoColumnSize) : null;
  // Across the hall (the frame plane) and along it.
  const colH = columnSize ?? col.h;
  const colB = columnSize ?? col.b;

  const vc = s - t - gd - colH / 2;
  const ue = a - t - gd - colB / 2;
  if (vc <= colH / 2 || ue <= colB / 2) return null;

  const bays = Math.max(1, Math.ceil((2 * ue) / bay - 1e-6));
  const bayLength = (2 * ue) / bays;
  const frames = Array.from({ length: bays + 1 }, (_, i) => -ue + i * bayLength);
  const rowsAt = Array.from({ length: rows }, (_, k) => -vc + ((k + 1) * 2 * vc) / (rows + 1));
  const rafterSpan = (2 * vc) / (rows + 1);
  const spacing = rows > 0 ? Math.min(cfg.interiorSpacing ?? 2, bays) : 1;
  // Frames with interior columns: every n-th, and always both end frames.
  const supported = frames.flatMap((_, i) => (i % spacing === 0 || i === bays ? [i] : []));

  // Steel binders resting on concrete columns span simply supported from column to column.
  const autoGirder =
    system === 'hybrid' ? girderProfileFor(rafterSpan, roofLoad * bayLength) : null;
  const rafterProfile = cfg.rafterProfile ?? autoGirder ?? autoRafter;
  const raf = STEEL_PROFILES[rafterProfile];

  /** Upward normal of the roof plane at `v` (across, up). */
  const normalAt = (v: number): readonly [number, number] =>
    flat ? [0, 1] : b.roofType === 'mono' || v < 0 ? [-sin, cos] : [sin, cos];
  const edge = s - t - gd;
  const rafterDepth = raf.h / cos;
  const rafterTop = (v: number) => roofAt(v) - pd / cos;
  const rafterAxis = (v: number) => rafterTop(v) - rafterDepth / 2;
  // Precast binders, one per field between column rows: flat underside, top along the roof,
  // about span / 30 deep at the supports (parallel binders under a flat roof span / 17).
  const girderSupport = Math.max(0.6, roundUp(rafterSpan / (flat ? 17 : 30), 0.05));
  // At most about span / 15 deep: under a steeper roof the underside rises towards the ridge.
  const girderDeepest = Math.max(girderSupport, roundUp(rafterSpan / 15, 0.05));
  type Field = readonly [number, number];
  const fields: Field[] = [-edge, ...rowsAt, edge].map((v0, i, all) => [v0, all[i + 1] ?? v0]);
  fields.pop();
  const level = ([v0, v1]: Field) => Math.min(rafterTop(v0), rafterTop(v1)) - girderSupport;
  const girderBottom = (f: Field, v: number) => Math.max(level(f), rafterTop(v) - girderDeepest);
  /** Where top or underside bend: the ends, the ridge and where the underside leaves the level. */
  const stations = (f: Field): number[] => {
    const [v0, v1] = f;
    const ridge = !flat && b.roofType !== 'mono' && v0 < -1e-6 && v1 > 1e-6;
    const tops = [v0, ...(ridge ? [0] : []), v1];
    const out: number[] = [];
    tops.forEach((va, i) => {
      out.push(va);
      const vb = tops[i + 1];
      if (vb === undefined) return;
      const ga = rafterTop(va) - girderDeepest - level(f);
      const gb = rafterTop(vb) - girderDeepest - level(f);
      if (ga * gb < 0) out.push(va + ((vb - va) * ga) / (ga - gb));
    });
    return out;
  };
  /** Underside of the rafter, or of the lower precast binder meeting at `v`. */
  const rafterBottom = (v: number) => {
    if (system !== 'precast') return rafterTop(v) - rafterDepth;
    const meeting = fields.filter(([v0, v1]) => v >= v0 - 1e-6 && v <= v1 + 1e-6);
    return Math.min(...meeting.map((f) => girderBottom(f, v)), rafterTop(v) - girderSupport);
  };

  const members: SteelMember[] = [];
  const plates: SteelPlate[] = [];
  const bars: ConcreteBar[] = [];
  const girders: ConcreteGirder[] = [];
  const across = dir(0, 1);
  const along = dir(1, 0);
  const up: Vec3 = { x: 0, y: 0, z: 1 };
  /** Column of a rolled profile, or of concrete `size` square where one is given. */
  const column = (
    kind: SteelMemberKind,
    profile: SteelProfileId,
    u: number,
    v: number,
    web: Vec3,
    axis?: string,
    top = rafterBottom(v),
    size: number | null = null,
  ) => {
    const named = axis !== undefined ? { axis } : {};
    if (size !== null)
      bars.push({ kind, a: p(u, v, 0), b: p(u, v, top), width: size, depth: size, web, ...named });
    else members.push({ kind, profile, a: p(u, v, 0), b: p(u, v, top), web, ...named });
  };
  /** Rafter from v0 to v1 (v0 < v1); its web stands perpendicular to it, pointing upwards. */
  const rafter = (u: number, v0: number, v1: number) => {
    const dz = rafterAxis(v1) - rafterAxis(v0);
    const len = Math.hypot(v1 - v0, dz) || 1;
    members.push({
      kind: 'rafter',
      roof: true,
      profile: rafterProfile,
      a: p(u, v0, rafterAxis(v0)),
      b: p(u, v1, rafterAxis(v1)),
      web: dir(0, -dz / len, (v1 - v0) / len),
    });
  };
  /** Round bar from (u0, v0, z0) to (u1, v1, z1); any web perpendicular to it will do. */
  const rod = (
    u0: number,
    v0: number,
    z0: number,
    u1: number,
    v1: number,
    z1: number,
    roof = false,
  ) => {
    const pa = p(u0, v0, z0);
    const pb = p(u1, v1, z1);
    const d = { x: pb.x - pa.x, y: pb.y - pa.y, z: pb.z - pa.z };
    const len = Math.hypot(d.x, d.y, d.z) || 1;
    const cx = -d.y;
    const cy = d.x;
    const cl = Math.hypot(cx, cy);
    const web = cl > 1e-6 ? { x: cx / cl, y: cy / cl, z: 0 } : { x: 1, y: 0, z: 0 };
    if (len > 0.3)
      members.push({
        kind: 'brace',
        profile: 'Rd 24',
        a: pa,
        b: pb,
        web,
        ...(roof ? { roof: true as const } : {}),
      });
  };

  const steelBeam = spacing > 1 && system !== 'precast';
  const autoBeam = steelBeam
    ? beamProfileFor(spacing * bayLength, spacing, roofLoad * bayLength * rafterSpan)
    : null;
  const beamProfile = steelBeam ? (cfg.beamProfile ?? autoBeam) : null;
  // A precast beam along the row carries the binders between its columns: about span / 12.
  const concreteBeam =
    spacing > 1 && system === 'precast'
      ? { width: 0.4, height: Math.max(0.6, roundUp((spacing * bayLength) / 12, 0.05)) }
      : null;
  const beamDepth = beamProfile ? STEEL_PROFILES[beamProfile].h : (concreteBeam?.height ?? 0);
  const inner = rowsAt.length > 0 ? interiorProfile(roofAt(0), spacing, roofLoad) : null;
  const innerSize = concrete
    ? (cfg.concreteColumn ?? concreteSize(roofAt(0), 0.4) + (spacing > 1 ? 0.05 : 0))
    : null;
  frames.forEach((u, i) => {
    const axis = String(i + 1);
    column('column', columnProfile, u, -vc, across, axis, rafterBottom(-vc), columnSize);
    column('column', columnProfile, u, vc, across, axis, rafterBottom(vc), columnSize);
    if (inner && supported.includes(i))
      for (const v of rowsAt)
        column('interiorColumn', inner, u, v, across, axis, rafterBottom(v) - beamDepth, innerSize);
    if (system === 'precast')
      for (const field of fields) {
        const at = stations(field);
        girders.push({
          top: at.map((v) => p(u, v, rafterTop(v))),
          bottom: at.map((v) => p(u, v, girderBottom(field, v))),
          normal: along,
          thickness: 0.4,
        });
      }
    else if (flat || b.roofType === 'mono') rafter(u, -edge, edge);
    else {
      rafter(u, -edge, 0);
      rafter(u, 0, edge);
    }
  });
  for (const v of rowsAt)
    supported.slice(1).forEach((k, j) => {
      const u0 = frames[supported[j] ?? 0] ?? 0;
      const u1 = frames[k] ?? 0;
      const z = rafterBottom(v) - beamDepth / 2;
      if (beamProfile)
        members.push({
          kind: 'beam',
          profile: beamProfile,
          a: p(u0, v, z),
          b: p(u1, v, z),
          web: up,
        });
      else if (concreteBeam)
        bars.push({
          kind: 'beam',
          roof: true,
          a: p(u0, v, z),
          b: p(u1, v, z),
          width: concreteBeam.width,
          depth: concreteBeam.height,
          web: up,
        });
    });

  const gable = cfg.gableColumns !== false;
  // Gable columns carry the wind on the gable wall more than roof.
  const gp = gableProfile(roofAt(0) * Math.sqrt(windFactor));
  const g = STEEL_PROFILES[gp];
  // Concrete ones: about 1/22 of their height, at least 30 cm, or what the wind needs.
  const gableSize = concrete
    ? Math.max(0.3, roundUp(roofAt(0) / 22, 0.05), windSize(roofAt(0), bay))
    : null;
  const gh = gableSize ?? g.h;
  if (gable) {
    const n = Math.ceil((2 * vc) / bay - 1e-6);
    for (let i = 1; i < n; i++) {
      const v = -vc + (i * 2 * vc) / n;
      if (rowsAt.some((r) => Math.abs(r - v) < 1)) continue;
      for (const side of [-1, 1])
        column(
          'gableColumn',
          gp,
          side * (a - t - gd - gh / 2),
          v,
          along,
          undefined,
          rafterBottom(v),
          gableSize,
        );
    }
  }

  if (purlins) {
    const planes: readonly (readonly [number, number])[] =
      flat || b.roofType === 'mono'
        ? [[-vc, vc]]
        : [
            [-vc, -0.15],
            [0.15, vc],
          ];
    const reach = a - t - gd;
    for (const [v0, v1] of planes) {
      const n = Math.max(1, Math.ceil((v1 - v0) / (PURLIN_SPACING * cos) - 1e-6));
      for (let i = 0; i <= n; i++) {
        const v = v0 + ((v1 - v0) * i) / n;
        const [nv, nz] = normalAt((v0 + v1) / 2);
        const cv = v - (nv * pd) / 2;
        const cz = roofAt(v) - (nz * pd) / 2;
        members.push({
          kind: 'purlin',
          roof: true,
          profile: purlinId,
          a: p(-reach, cv, cz),
          b: p(reach, cv, cz),
          web: dir(0, nv, nz),
        });
      }
    }
  }

  if (girts) {
    const levels = girtLevels(e);
    for (const z of levels)
      for (const side of [-1, 1]) {
        const v = side * (s - t - gd / 2);
        members.push({
          kind: 'girt',
          profile: girtId,
          a: p(-(a - t), v, z),
          b: p(a - t, v, z),
          web: across,
        });
        const u = side * (a - t - gd / 2);
        members.push({
          kind: 'girt',
          profile: girtId,
          a: p(u, -(s - t - gd), z),
          b: p(u, s - t - gd, z),
          web: along,
        });
      }
  }

  if (bracing) {
    const strut: SectionId = bayLength <= 6.01 ? 'RR 120' : 'RR 140';
    const sh = sectionOf(strut).h;
    const struts = [-vc, vc, ...(flat || b.roofType === 'mono' || rows > 0 ? [] : [0])];
    for (let i = 0; i < bays; i++) {
      const u0 = (frames[i] ?? 0) + colB / 2;
      const u1 = (frames[i + 1] ?? 0) - colB / 2;
      for (const v of struts) {
        const z = rafterBottom(v) - sh / 2 - 0.01;
        members.push({
          kind: 'strut',
          profile: strut,
          a: p(u0, v, z),
          b: p(u1, v, z),
          web: up,
          ...(v === 0 ? { roof: true as const } : {}),
        });
      }
    }
    const braced = bays >= 3 ? [0, bays - 1] : [0];
    const roofPlanes: readonly (readonly [number, number])[] =
      flat || b.roofType === 'mono'
        ? [[-vc, vc]]
        : [
            [-vc, 0],
            [0, vc],
          ];
    for (const i of braced) {
      const u0 = frames[i] ?? 0;
      const u1 = frames[i + 1] ?? 0;
      for (const [v0, v1] of roofPlanes) {
        const k = Math.max(1, Math.round((v1 - v0) / bayLength));
        for (let j = 0; j < k; j++) {
          const w0 = v0 + ((v1 - v0) * j) / k;
          const w1 = v0 + ((v1 - v0) * (j + 1)) / k;
          const z = (v: number) => rafterTop(v) - 0.03;
          rod(u0, w0, z(w0), u1, w1, z(w1), true);
          rod(u0, w1, z(w1), u1, w0, z(w0), true);
        }
      }
      if (wallBracing)
        for (const v of [-vc, vc]) {
          const top = rafterBottom(v) - sh - 0.15;
          rod(u0 + colB / 2, v, 0.3, u1 - colB / 2, v, top);
          rod(u0 + colB / 2, v, top, u1 - colB / 2, v, 0.3);
        }
    }
  }

  if (haunches) {
    const length = Math.min(3, Math.max(0.6, rafterSpan / 10));
    const normal = along;
    for (const u of frames)
      for (const side of [-1, 1]) {
        const vf = side * (vc - colH / 2);
        const vt = vf - side * length;
        plates.push({
          corners: [
            p(u, vf, rafterBottom(vf)),
            p(u, vf, rafterBottom(vf) - raf.h),
            p(u, vt, rafterBottom(vt)),
          ],
          normal,
          thickness: raf.b,
        });
      }
  }

  const columns = [
    ...members.filter((m) => VERTICAL.includes(m.kind)).map(columnFootprint),
    ...bars.filter((m) => VERTICAL.includes(m.kind)).map(concreteFootprint),
  ];
  const tx = modelTexts().hallStructure;
  const hints: string[] = [];
  if (system === 'hybrid' && rafterSpan > TRUSS_SPAN)
    hints.push(fillText(tx.trussGirder, { span: formatNumber(rafterSpan, 1, 1) }));
  if (system === 'precast' && rafterSpan > PRECAST_SPAN)
    hints.push(fillText(tx.precastSpan, { span: formatNumber(rafterSpan, 1, 1) }));
  if (system === 'precast' && !flat && tan > PRECAST_SLOPE)
    hints.push(fillText(tx.steepPrecast, { pitch: formatNumber(b.roofPitchDeg, 1) }));
  const deepest = Math.max(
    0,
    ...girders.flatMap((gr) => gr.top.map((q, i) => q.z - (gr.bottom[i]?.z ?? q.z))),
  );
  // Names sit outside the hall on the sides without the size dimensions (north and west).
  const named = alongX ? 1 : -1;
  const xy = (u: number, v: number): Vec2 => {
    const q = p(u, v, 0);
    return { x: q.x, y: q.y };
  };
  const rowLines = [-vc, ...rowsAt, vc].sort((x, y) => named * (y - x));
  const axes: GridAxis[] = [
    ...frames.map((u, i) => ({
      label: String(i + 1),
      a: xy(u, -named * s),
      b: xy(u, named * s),
    })),
    ...rowLines.map((v, i) => ({
      label: String.fromCharCode(65 + i),
      a: xy(named * a, v),
      b: xy(-named * a, v),
    })),
  ];

  return {
    system,
    members,
    plates,
    bars,
    girders,
    columns,
    columnSize,
    girder: system === 'precast' ? { support: girderSupport, deepest } : null,
    hints,
    axes,
    bays,
    bayLength,
    interiorRows: rows,
    interiorSpacing: spacing,
    beamProfile,
    columnProfile,
    rafterProfile,
    girts,
    auto: {
      interiorRows: autoRows(2 * s, system),
      columnProfile: autoColumn,
      rafterProfile: autoGirder ?? autoRafter,
      beamProfile: autoBeam,
      columnSize: autoColumnSize,
      girts: facadeNeedsGirts(b),
    },
    rafterSpan,
    roofLoad,
    windLoad: loads.wind,
    clearWidth: 2 * (s - t - gd - colH),
    clearLength: 2 * (a - t - gd - Math.max(colB, gable ? gh : 0)),
  };
}

/** Door, gate or window of the hall that a column of the steel frame stands in. */
export interface StructureConflict {
  readonly openingId: string;
  readonly opening: Opening;
  /** Frame axis of the column; null for a gable wall column. */
  readonly axis: string | null;
}

export interface HallSteel extends HallFrame {
  readonly conflicts: readonly StructureConflict[];
}

type StructureDoc = Pick<ProjectDocument, 'walls' | 'openings'> & {
  /** Site loads; missing means the usual snow load. */
  readonly settings?: Pick<ProjectSettings, 'loads'>;
};

const structureCache = new WeakMap<
  Building,
  {
    walls: StructureDoc['walls'];
    openings: StructureDoc['openings'];
    loads: HallLoads;
    result: HallSteel | null;
  }
>();

const unit = (w: Wall): Vec2 => {
  const dx = w.end.x - w.start.x;
  const dy = w.end.y - w.start.y;
  const l = Math.hypot(dx, dy) || 1;
  return { x: dx / l, y: dy / l };
};
const alongWall = (w: Wall, q: Vec2): number => {
  const d = unit(w);
  return (q.x - w.start.x) * d.x + (q.y - w.start.y) * d.y;
};

/**
 * The hall's steel frame fitted to its exterior walls: girts stop at doors, gates and
 * windows, and openings a column of the frame stands in are reported.
 */
export function hallSteel(doc: StructureDoc, b: Building): HallSteel | null {
  const loads = projectHallLoads(doc, b);
  const cached = structureCache.get(b);
  if (
    cached &&
    cached.walls === doc.walls &&
    cached.openings === doc.openings &&
    cached.loads.roof === loads.roof &&
    cached.loads.wind === loads.wind
  )
    return cached.result;
  const result = computeHallSteel(doc, b, loads);
  structureCache.set(b, { walls: doc.walls, openings: doc.openings, loads, result });
  return result;
}

function computeHallSteel(doc: StructureDoc, b: Building, loads: HallLoads): HallSteel | null {
  const frame = hallFrame(b, loads);
  if (!frame) return null;
  const walls = Object.values(doc.walls).filter((w) => w.ownerId === b.id && w.level === 0);
  const openings = new Map<string, Opening[]>(walls.map((w) => [w.id, []]));
  for (const o of Object.values(doc.openings)) openings.get(o.wallId)?.push(o);
  const reach = b.wallThickness + 1;
  /** Wall nearest to `q` (within about a metre), optionally only parallel to `d`. */
  const wallAt = (q: Vec2, d?: Vec2): Wall | undefined => {
    let best: { w: Wall; dist: number } | undefined;
    for (const w of walls) {
      if (d) {
        const u = unit(w);
        if (Math.abs(u.x * d.y - u.y * d.x) > 0.01) continue;
      }
      const dist = distancePointToSegment(q, { a: w.start, b: w.end });
      if (dist < reach && (!best || dist < best.dist)) best = { w, dist };
    }
    return best?.w;
  };

  const members = frame.members.flatMap((m): SteelMember[] => {
    if (m.kind !== 'girt') return [m];
    const d = { x: m.b.x - m.a.x, y: m.b.y - m.a.y };
    const len = Math.hypot(d.x, d.y) || 1;
    const w = wallAt({ x: (m.a.x + m.b.x) / 2, y: (m.a.y + m.b.y) / 2 }, d);
    const list = w ? (openings.get(w.id) ?? []) : [];
    const half = sectionOf(m.profile).b / 2;
    const cuts = list
      .filter((o) => o.sillHeight < m.a.z + half && o.sillHeight + o.height > m.a.z - half)
      .map((o) => [o.offsetFromWallStart - 0.05, o.offsetFromWallStart + o.width + 0.05] as const);
    if (!w || cuts.length === 0) return [m];
    // Girt ends in the wall's own coordinate along it.
    const sa = alongWall(w, m.a);
    const sb = alongWall(w, m.b);
    const [lo, hi] = sa < sb ? [sa, sb] : [sb, sa];
    const pieces: [number, number][] = [];
    let from = lo;
    for (const [c0, c1] of [...cuts].sort((x, y) => x[0] - y[0])) {
      if (c0 > from) pieces.push([from, Math.min(c0, hi)]);
      from = Math.max(from, c1);
    }
    if (from < hi) pieces.push([from, hi]);
    const at = (sw: number): Vec3 => {
      const k = (sw - sa) / (sb - sa || 1);
      return { x: m.a.x + d.x * k, y: m.a.y + d.y * k, z: m.a.z };
    };
    return pieces
      .filter(([x0, x1]) => x1 - x0 > Math.min(0.3, len))
      .map(([x0, x1]) => ({ ...m, a: at(x0), b: at(x1) }));
  });

  const conflicts: StructureConflict[] = [];
  const seen = new Set<string>();
  for (const c of frame.columns) {
    if (c.kind === 'interiorColumn') continue;
    // Corner columns stand behind two walls.
    for (const w of walls) {
      if (distancePointToSegment(c.center, { a: w.start, b: w.end }) > reach) continue;
      const d = unit(w);
      const cosA = Math.abs(Math.cos(c.obb.rotation) * d.x + Math.sin(c.obb.rotation) * d.y);
      const sinA = Math.sqrt(Math.max(0, 1 - cosA * cosA));
      const half = (cosA * c.obb.width + sinA * c.obb.depth) / 2;
      const sc = alongWall(w, c.center);
      for (const o of openings.get(w.id) ?? []) {
        if (seen.has(o.id)) continue;
        if (o.offsetFromWallStart < sc + half && o.offsetFromWallStart + o.width > sc - half) {
          seen.add(o.id);
          conflicts.push({ openingId: o.id, opening: o, axis: c.axis ?? null });
        }
      }
    }
  }
  return { ...frame, members, conflicts };
}
